import React, {
  useCallback, useEffect, useMemo, useState, ReactElement,
} from 'react';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import ActionButton from '../../../components/action-button/action-button.component';
import Widget from '../../../components/widget/widget.component';
import ChartLine from '../../../components/chart-line/chart-line.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import TableRowAddNewItem from '../../../components/table-row-add-new-item/table-row-add-new-item.component';
import RemoveIcon from '../../../icons/remove-icon';
import StructureService from '../../../services/structure-service';
import FormatService from '../../../services/format-service';
import CurrencyService from '../../../services/currency-service';
import FormService from '../../../services/form-service';
import {
  DEAL_HISTORICAL_VALUATIONS_CREATE, DEAL_HISTORICAL_VALUATIONS_DELETE, DEAL_HISTORICAL_VALUATIONS_EDIT,
} from '../../../constants/entity/permissions';
import CURRENCY_USD from '../../../constants/system/currencies';

const DealHistoricalValuations: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'Historical Valuations';
  const WIDGET_NAME: string = 'widget-deal-historical-valuations';
  const ID_CONTAINER_CHART_DEAL_HISTORICAL_VALUATIONS: string = 'container-chart-deal-historical-valuations';
  const COLUMNS_TOTAL: number = 2;

  const {
    isEditMode,
    isNewDeal,
    dealHistoricalValuationsData,
    dealHistoricalValuationsNewItems,
    onAddNewItem,
    onDeleteClick,
    onEditModeButtonClick,
  } = props;

  const [dealHistoricalValuationsItems, setDealHistoricalValuationsItems]
    : [Record<string, DealHistoricalValuationsItem> | null, Function] = useState<StateValueDefault>(null);

  const dealHistoricalValuationsEditPermissions: string[] = useMemo((): string[] => [
    DEAL_HISTORICAL_VALUATIONS_EDIT,
  ], []);
  const dealHistoricalValuationsCreatePermissions: string[] = useMemo((): string[] => [
    DEAL_HISTORICAL_VALUATIONS_CREATE,
  ], []);
  const dealHistoricalValuationsDeletePermissions: string[] = useMemo((): string[] => [
    DEAL_HISTORICAL_VALUATIONS_DELETE,
  ], []);

  const formPrefix: string = 'dealData.dealHistoricalValuationsData';
  const isEmpty: boolean = !isEditMode && !isNewDeal
    && (!dealHistoricalValuationsData || !dealHistoricalValuationsData.length);
  const dealHistoricalValuationItemValues: DealHistoricalValuationsItem[] = (dealHistoricalValuationsItems
    && Object.values(dealHistoricalValuationsItems)) || [];
  const currencySign: string | null = CurrencyService.getCurrencySign(CURRENCY_USD);
  const chartItemList: ChartLineItem[] = dealHistoricalValuationsData && dealHistoricalValuationsData.length
    ? dealHistoricalValuationsData.map((
      dealHistoricalValuationItem: DealHistoricalValuationsItem,
    ): ChartLineItem => ({
      name: dealHistoricalValuationItem.name,
      value: FormatService.parseInt(dealHistoricalValuationItem.value),
    })) : [];

  const addNewItem: MouseEventHandlerFunction = useCallback((): void => {
    if (onAddNewItem) {
      onAddNewItem({
        id: StructureService.getNewItemId(dealHistoricalValuationsNewItems, dealHistoricalValuationsData),
        name: '',
        value: '',
      });
    }
  }, [dealHistoricalValuationsData, dealHistoricalValuationsNewItems]);

  const deleteClick: Function = useCallback((dealHistoricalValuationsItemId: number): void => {
    if (onDeleteClick) {
      onDeleteClick(dealHistoricalValuationsItemId);
    }

    if (dealHistoricalValuationsItems) {
      const newDealHistoricalValuationsItems: Record<string, DealHistoricalValuationsItem> = {
        ...dealHistoricalValuationsItems,
      };
      delete newDealHistoricalValuationsItems[dealHistoricalValuationsItemId];
      setDealHistoricalValuationsItems(newDealHistoricalValuationsItems);
    }
  }, [dealHistoricalValuationsItems, dealHistoricalValuationsNewItems]);

  useEffect((): void => {
    if (dealHistoricalValuationsData) {
      const initialDealHistoricalValuationsItems: Record<string, DealHistoricalValuationsItem> = {};

      dealHistoricalValuationsData.forEach((dealHistoricalValuationsItem: DealHistoricalValuationsItem): void => {
        initialDealHistoricalValuationsItems[dealHistoricalValuationsItem.id] = { ...dealHistoricalValuationsItem };
      });

      setDealHistoricalValuationsItems(initialDealHistoricalValuationsItems);
    }
  }, [dealHistoricalValuationsData, isEditMode]);

  return (
    <CheckAccess permissions={dealHistoricalValuationsEditPermissions} isEnabled={isEditMode || isNewDeal}>
      <Widget
        isScrollable={isEditMode}
        isEmpty={isEmpty}
        isEditMode={isEditMode}
        withEditMode={!isNewDeal}
        editPermissions={dealHistoricalValuationsEditPermissions}
        widgetName={WIDGET_NAME}
        defaultWidgetTitle={WIDGET_TITLE_DEFAULT}
        onEditModeButtonClick={onEditModeButtonClick}
      >
        {
          !isEditMode && !isNewDeal && dealHistoricalValuationsData && !!dealHistoricalValuationsData.length && (
            <ChartLine
              containerId={ID_CONTAINER_CHART_DEAL_HISTORICAL_VALUATIONS}
              chartItemList={chartItemList}
              labelFormatter={(value: number): string => (
                `${currencySign}${FormatService.formatRoundedBigNumber(value.toString())}`
              )}
            />
          )
        }
        {
          (isEditMode || isNewDeal) && (
            <CheckAccess permissions={dealHistoricalValuationsEditPermissions}>
              <Table>
                <TableBody isPlain withZebraRows withHeaderColumn>
                  {
                    [
                      ...dealHistoricalValuationItemValues,
                      ...(
                        dealHistoricalValuationsNewItems.filter((
                          dealHistoricalValuationsItem: DealHistoricalValuationsItem,
                        ): boolean => (
                          !dealHistoricalValuationItemValues.find((
                            dealHistoricalValuationsItemValue: DealHistoricalValuationsItem,
                          ): boolean => (
                            dealHistoricalValuationsItemValue.id === dealHistoricalValuationsItem.id
                          ))
                        )) || []
                      ),
                    ].map((dealHistoricalValuationsItem: DealHistoricalValuationsItem): ReactElement => {
                      const isNewItem: boolean = dealHistoricalValuationsNewItems
                        && !!dealHistoricalValuationsNewItems.length
                        && !!dealHistoricalValuationsNewItems.find((
                          dealHistoricalValuationsNewItem: DealHistoricalValuationsItem,
                        ): boolean => (
                          dealHistoricalValuationsNewItem.id === dealHistoricalValuationsItem.id
                        ));
                      const formKeyDealHistoricalValuationsItem: string = FormService.getIndexedFormFieldKey(
                        dealHistoricalValuationsItem.id,
                      );

                      return (
                        <TableRow isActive={isNewItem && !isNewDeal} key={dealHistoricalValuationsItem.id}>
                          <TableCell>
                            <FormFieldText
                              isHidden
                              name={`${formPrefix}[${formKeyDealHistoricalValuationsItem}].id`}
                            />
                            <FormFieldText
                              name={`${formPrefix}[${formKeyDealHistoricalValuationsItem}].name`}
                              placeholder="Name"
                            />
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isNewDeal) && (
                                <FlexContainer>
                                  <FormFieldNumber
                                    name={`${formPrefix}[${formKeyDealHistoricalValuationsItem}].value`}
                                    placeholder="Value"
                                  />
                                  <CheckAccess permissions={dealHistoricalValuationsDeletePermissions}>
                                    <div>
                                      <Tooltip title="Delete">
                                        <ActionButton
                                          isInline
                                          withNoPadding
                                          onClick={(): void => {
                                            deleteClick(dealHistoricalValuationsItem.id);
                                          }}
                                        >
                                          <RemoveIcon />
                                        </ActionButton>
                                      </Tooltip>
                                    </div>
                                  </CheckAccess>
                                </FlexContainer>
                              )
                            }
                          </TableCell>
                        </TableRow>
                      );
                    })
                  }
                  {
                    (isEditMode || isNewDeal) && (
                      <CheckAccess permissions={dealHistoricalValuationsCreatePermissions}>
                        <TableRowAddNewItem colSpan={COLUMNS_TOTAL} onClick={addNewItem} />
                      </CheckAccess>
                    )
                  }
                </TableBody>
              </Table>
            </CheckAccess>
          )
        }
      </Widget>
    </CheckAccess>
  );
});

export default DealHistoricalValuations;
